// Chaves PIX do seller, croqui `screens/seller/chaves-pix.tsx` (WEB-62).
//
// Foco da tela: o DOCUMENTO do KYC. Ele é a chave (`spec/09` §4.1, FIX-PIX-LOOKUP), então
// o card do documento vem antes da tabela e o resto é consequência dele.
//
// O estado da adquirente é POR LINHA (`partner_status`, API-29), não mais um `Notice`
// agregado de `counters`: uma chave recusada só aparece na linha dela.
//
// Quem tem `useQuery`/`useMutation` é esta rota; os componentes de `features/pix-keys`
// recebem dado por prop, inclusive o perfil, que antes o dialog de cadastro buscava por
// conta própria (item 4 das "Questões abertas" de FIX-PIX-LOOKUP, agora fechado).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { profileQueryOptions } from '../api/queries/account.ts';
import {
  type CreatePixKeyBody,
  createPixKey,
  deactivatePixKey,
  PIX_KEYS_QUERY_KEY,
  type PixKey,
  pixKeysQueryOptions,
} from '../api/queries/pix-keys.ts';
import { countersQueryOptions } from '../api/queries/shell.ts';
import { useMode } from '../app/mode.tsx';
import { Btn } from '../components/kit/btn.tsx';
import { Notice } from '../components/kit/notice.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { CreatePixKeyDrawer } from '../features/pix-keys/create-pix-key-drawer.tsx';
import { KycDocumentCard } from '../features/pix-keys/kyc-document-card.tsx';
import {
  activeCount,
  createBlockedReason,
  createErrorMessage,
  isStepUpCancelled,
  MAX_ACTIVE_PIX_KEYS,
} from '../features/pix-keys/model.ts';
import { type PixKeyRowError, PixKeysTable } from '../features/pix-keys/pix-keys-table.tsx';
import { RemovePixKeyDrawer } from '../features/pix-keys/remove-pix-key-drawer.tsx';

function PixKeys() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const keys = useQuery(pixKeysQueryOptions(mode));
  const profile = useQuery(profileQueryOptions(mode));

  const [createOpen, setCreateOpen] = useState(false);
  const [toRemove, setToRemove] = useState<PixKey | undefined>(undefined);
  const [rowError, setRowError] = useState<PixKeyRowError | undefined>(undefined);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [PIX_KEYS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: countersQueryOptions(mode).queryKey }),
    ]);
  };

  const create = useMutation({
    mutationFn: async (body: CreatePixKeyBody) => createPixKey(mode, body),
    onSuccess: async () => {
      setCreateOpen(false);
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: async (key: PixKey) => {
      setRowError(undefined);
      return deactivatePixKey(mode, key.id);
    },
    onSuccess: () => {
      setToRemove(undefined);
    },
    onError: (error, key) => {
      setToRemove(undefined);
      if (!isStepUpCancelled(error)) {
        setRowError({ keyId: key.id, message: createErrorMessage(error) });
      }
    },
    onSettled: refresh,
  });

  const rows = keys.data ?? [];
  const blocked = createBlockedReason(profile.data, rows);

  const openCreate = (): void => {
    create.reset();
    setCreateOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Chaves PIX"
        sub="A chave de saque é o documento do seu cadastro, sem consulta a banco"
        path="/v1/me/pix-keys"
        size="lg"
        actions={
          <Btn kind="primary" icon="key" disabled={blocked !== null} onClick={openCreate}>
            Cadastrar chave
          </Btn>
        }
      />

      <KycDocumentCard profile={profile.data} />

      {/* O motivo do bloqueio é TEXTO na tela, não só o `disabled` do botão: um botão
          apagado sem explicação é a tela escondendo a razão (`spec/17` §7 item 12). Aviso
          quando há o que resolver, linha discreta quando o bloqueio É o sucesso da tela -
          ver `CreateBlock` em `features/pix-keys/model.ts`. */}
      {blocked?.kind === 'alert' && (
        <Notice tone={blocked.tone} icon="alert" title={blocked.reason}>
          A chave PIX é o CPF/CNPJ aprovado na verificação, você não escolhe nem digita chave.
        </Notice>
      )}
      {blocked?.kind === 'quiet' && <p className="-mt-1 text-12 text-muted">{blocked.reason}</p>}

      <Notice tone="neutral" icon="lock" title="Toda ação aqui exige step-up.">
        Senha atual ou código do app autenticador, sem exceção, mesmo com a 2FA desligada.
      </Notice>

      <Panel
        title="Suas chaves"
        sub={
          keys.isPending
            ? 'Carregando…'
            : `${activeCount(rows)} de ${MAX_ACTIVE_PIX_KEYS} ativas · o saque só sai para chave aprovada pela adquirente`
        }
      >
        <PixKeysTable
          keys={rows}
          isLoading={keys.isPending}
          // Erro só quando não há lista: com o polling de 30 s, um flake num refetch de
          // fundo trocaria a lista carregada por uma tela de falha.
          error={keys.data === undefined ? keys.error : undefined}
          onRetry={() => {
            void keys.refetch();
          }}
          onRemove={setToRemove}
          onCreate={openCreate}
          createBlockedReason={blocked?.reason ?? null}
          {...(rowError === undefined ? {} : { rowError })}
        />
      </Panel>

      <CreatePixKeyDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        profile={profile.data}
        isPending={create.isPending}
        error={create.error}
        onSubmit={(body) => {
          create.mutate(body);
        }}
      />

      <RemovePixKeyDrawer
        pixKey={toRemove}
        onOpenChange={(open) => {
          if (!open) {
            setToRemove(undefined);
          }
        }}
        isPending={remove.isPending}
        onConfirm={(key) => {
          remove.mutate(key);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/app/settings/pix-keys/')({ component: PixKeys });
